import { useTheme } from "@mui/material";
import * as React from "react";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";

const wrapText = (text: string, maxChars: number): string[] => {
  const lines = [];
  for (let i = 0; i < text.length; i += maxChars) {
    lines.push(text.slice(i, i + maxChars));
  }
  return lines;
};

const createTickWithData = (data: RadarData[]) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function CustomTick({ payload, cx, cy, textAnchor, radius }: any) {
    const theme = useTheme();

    const angle = (payload.coordinate * Math.PI) / 180;
    const lines = wrapText(payload.value, 6); // ここで改行の文字数指定
    const labelRadius = radius + 12; // ラベルの距離（半径 + 任意の距離）

    const x = cx + labelRadius * Math.cos(angle);
    const y = cy - labelRadius * Math.sin(angle);

    const disabledLabels = data.filter(item => item.value === 0).map(item => item.subject);

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={disabledLabels && disabledLabels.includes(payload.value) ? theme.palette.action.disabled : theme.palette.text.secondary}
        fontSize={12}
      >
        {lines.map((line, index) => (
          <tspan x={x} dy={index === 0 ? 0 : 14} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomDot = (props: any) => {
  const { cx, cy, payload, dataKey } = props;

  const value = payload[dataKey];
  if (value === 0) {
    return null;
  }

  return <circle cx={cx} cy={cy} r={4} fill="#00E1FF" strokeWidth={0} />;
};

type RadarData = {
  subject: string;
  value: number;
  fullMark: number;
};

export type AIRadarChartProps = {
  data: RadarData[];
  name: string;
};

const AIRadarChart: React.FC<AIRadarChartProps> = props => {
  const CustomTick = createTickWithData(props.data);
  const fullMark = props.data.reduce((max, item) => Math.max(max, item.fullMark), 0);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={props.data} startAngle={90} endAngle={-270}>
        <defs>
          <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1" opacity={0.5}>
            <stop stopColor="#00E1FF" />
            <stop offset="0.5" stopColor="#2549E7" />
            <stop offset="1" stopColor="#1DB07D" />
          </linearGradient>
        </defs>
        <PolarGrid stroke="#274347" />
        <PolarRadiusAxis domain={[0, fullMark]} stroke="#274347" tick={false} />
        <PolarAngleAxis dataKey="subject" tick={<CustomTick />} label={{ position: "outside", offset: 10 }} />
        <Radar
          name={props.name}
          dataKey="value"
          stroke="#00E1FF"
          fill="url(#radarGradient)"
          fillOpacity={0.5}
          dot={<CustomDot />}
          activeDot={{ r: 7, fill: "#00E1FF", stroke: "#00E1FF", strokeWidth: 2, opacity: 1 }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
};

AIRadarChart.displayName = "AIRadarChart";

export default AIRadarChart;
